class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);
    // Discrete controls (checkbox, radio, select) have no rapid-fire to coalesce,
    // so an 800ms debounce there is just ~1s of dead perceived lag. Submit them on
    // the next tick and reserve the long debounce for price-range text/number
    // inputs that fire on every keystroke.
    this.quickOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 0);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      // Quick-submit ONLY discrete controls (checkbox/radio/select), which have no
      // rapid-fire to coalesce. Everything else — price-range text/number inputs
      // AND the in-filter search box — keeps the 800ms debounce so we never fire a
      // fetch per keystroke.
      const type = (event.target && event.target.type) || '';
      const isDiscrete =
        type === 'checkbox' || type === 'radio' || type === 'select-one' || type === 'select-multiple';
      if (isDiscrete) {
        this.quickOnSubmit(event);
      } else {
        this.debouncedOnSubmit(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      // Derive the filter params from history state when present, but fall back
      // to the actual URL (not searchParamsInitial) whenever state is missing or
      // was written by another engine — e.g. the filter drawer pushes { path }.
      // Without this fallback, event.state.searchParams is `undefined` and
      // renderPage builds a malformed `?section_id=…&undefined` request that the
      // server answers with the UNFILTERED catalog, repainting the full catalog
      // while the URL still shows a filtered query on Back/Forward.
      const searchParams =
        event.state && event.state.searchParams !== undefined
          ? event.state.searchParams
          : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    // Abort any in-flight filter fetch so a slower earlier request can't resolve
    // late and overwrite this newer one (stale-swap guard, fetch phase).
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static parseHTML(html) {
    if (typeof html === 'string') return new DOMParser().parseFromString(html, 'text/html');
    return html;
  }

  // Cached desktop MediaQueryList — `.matches` is always live, so we only pay the
  // matchMedia construction once instead of on every grid swap.
  static get isDesktopMQL() {
    if (!FacetFiltersForm._desktopMQL) {
      FacetFiltersForm._desktopMQL = window.matchMedia('(min-width: 990px)');
    }
    return FacetFiltersForm._desktopMQL;
  }

  // Append to the filter-data cache, de-duping by url and evicting the oldest
  // entries past the cap (FIFO). The cap is the fix for the old unbounded growth
  // that retained a detached Document per visited combination forever. Entries stay
  // parsed Documents (not re-parsed strings) so a cache HIT — which lands on the
  // click path — is an instant reuse instead of a synchronous DOMParser pass.
  static pushFilterData(entry) {
    const max = FacetFiltersForm.FILTER_CACHE_MAX || 8;
    const next = FacetFiltersForm.filterData.filter((e) => e.url !== entry.url);
    next.push(entry);
    while (next.length > max) next.shift();
    FacetFiltersForm.filterData = next;
  }

  // Hand the just-fetched grid-section document to any listener (the filter
  // drawer) so it can refresh its checkbox counts from the section's facets we
  // already fetched, instead of doing its own second full-page round-trip.
  static notifyDrawerFacets(parsed) {
    document.dispatchEvent(new CustomEvent('facets:filters-updated', { detail: { doc: parsed } }));
  }

  static renderSectionFromFetch(url, event) {
    const controller = FacetFiltersForm.abortController;
    fetch(url, controller ? { signal: controller.signal } : {})
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = FacetFiltersForm.parseHTML(responseText);
        FacetFiltersForm.pushFilterData({ html: parsed, url });
        FacetFiltersForm.renderFilters(parsed, event);
        FacetFiltersForm.notifyDrawerFacets(parsed);
        FacetFiltersForm.renderProductGridContainer(parsed);
        FacetFiltersForm.renderProductCount(parsed);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
      })
      .catch((error) => {
        // A newer filter request aborted this one — it now owns the loading state,
        // so bail without logging or clearing spinners (which would flash the UI).
        if (error.name === 'AbortError') return;
        // Error handling: Log error and reset UI loading states to prevent stuck spinners
        console.error('Error fetching facet filters:', error);
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
        const gridContainer = document.getElementById('ProductGridContainer');
        if (gridContainer) {
          const collection = gridContainer.querySelector('.collection');
          if (collection) collection.classList.remove('loading');
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsed = FacetFiltersForm.parseHTML(html);
    FacetFiltersForm.renderFilters(parsed, event);
    FacetFiltersForm.notifyDrawerFacets(parsed);
    // Instant swap — this category is already cached (HTML + images warm), so skip
    // the dim/decode loading state and render it immediately.
    FacetFiltersForm.renderProductGridContainer(parsed, true);
    FacetFiltersForm.renderProductCount(parsed);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
  }

  // Seed the cache with the currently displayed grid (notably the initial,
  // server-rendered category, which is otherwise never cached) under its filter
  // key, so the first return to it is an instant cache hit instead of a fresh
  // fetch + loading state. No-ops when that key is already cached — AJAX-swapped
  // grids cache themselves on fetch. `searchParams` must be built the same way a
  // filter click builds it, so the key matches the later lookup byte-for-byte.
  static cacheCurrentSection(searchParams) {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.id) return;
    const url = `${window.location.pathname}?section_id=${grid.dataset.id}&${searchParams}`;
    if (FacetFiltersForm.filterData.some((entry) => entry.url === url)) return;
    FacetFiltersForm.pushFilterData({ html: FacetFiltersForm.parseHTML(document.documentElement.outerHTML), url });
  }

  static renderProductGridContainer(parsed, instant = false) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const parsedContainer = parsed.getElementById('ProductGridContainer');
    if (!parsedContainer) return;

    parsedContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    // Pass the already-parsed node (not a string) so swapGridWhenReady can read
    // above-the-fold <img>s directly instead of re-parsing the HTML a second time.
    FacetFiltersForm.swapGridWhenReady(container, parsedContainer, instant);
  }

  // Preload + DECODE the incoming grid's above-the-fold images as bare <img> clones
  // (same srcset/sizes → same candidate the real cards will use). Resolves once they
  // have decoded (or capMs elapses). Warming the browser's decoded-image cache first
  // means the real cards paint already-decoded instead of blank-then-image — even
  // for a cached grid, whose freshly-created decoding="async" <img>s would otherwise
  // re-decode and popcorn.
  static preloadAboveFold(parsedContainer, capMs) {
    return new Promise((resolve) => {
      let sources = [];
      try {
        let perRow = 4;
        const liveGrid = document.getElementById('product-grid');
        if (liveGrid) {
          const isDesktop = FacetFiltersForm.isDesktopMQL.matches;
          const match = liveGrid.className.match(
            isDesktop ? /grid--(\d+)-col-desktop/ : /grid--(\d+)-col-tablet-down/
          );
          if (match) perRow = parseInt(match[1], 10) || perRow;
        }
        const aboveFold = Math.max(2, perRow * 2);
        sources = Array.from(
          parsedContainer.querySelectorAll('.card-image-slider__list .slider__slide:first-child img')
        ).slice(0, aboveFold);
      } catch (e) {
        resolve();
        return;
      }
      if (!sources.length) {
        resolve();
        return;
      }

      const stage = document.createElement('div');
      stage.setAttribute('aria-hidden', 'true');
      stage.style.cssText =
        'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;';
      const clones = sources.map((src) => {
        const img = document.createElement('img');
        const srcset = src.getAttribute('srcset');
        const sizes = src.getAttribute('sizes');
        if (srcset) img.setAttribute('srcset', srcset);
        if (sizes) img.setAttribute('sizes', sizes);
        img.setAttribute('src', src.getAttribute('src') || '');
        img.loading = 'eager';
        stage.appendChild(img);
        return img;
      });
      document.body.appendChild(stage);

      const done = () => {
        if (stage.parentNode) stage.remove();
        resolve();
      };
      if (typeof clones[0].decode !== 'function') {
        done();
        return;
      }
      const decoded = Promise.all(clones.map((img) => img.decode().catch(() => {})));
      const cap = new Promise((r) => setTimeout(r, capMs));
      Promise.race([decoded, cap]).then(done);
    });
  }

  // Double-buffer the grid swap: keep the current grid visible (dimmed) while the
  // incoming grid loads HIDDEN in the background; once its above-the-fold images
  // have decoded, reveal the whole new grid at once. No blank gap, no per-image
  // popcorn. Matches: click -> grid fades out -> new grid loads in background ->
  // whole new grid appears together.
  static swapGridWhenReady(container, parsedContainer, instant = false) {
    const newHTML = parsedContainer.innerHTML;
    // Bump a generation token so a slow background load that finishes AFTER a newer
    // filter click can't overwrite the newer grid (stale-swap guard).
    const generation = (FacetFiltersForm.swapGeneration = (FacetFiltersForm.swapGeneration || 0) + 1);

    const apply = () => {
      // Remove the swap state BEFORE inserting so the incoming #product-grid is
      // created at full opacity and appears at once with NO fade-in.
      container.classList.remove('is-swapping');
      container.innerHTML = newHTML;
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(container);
      }
      // Defer the eager-image upgrade one frame so the new grid paints first — its
      // querySelectorAll + loop shouldn't pad the swap task.
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => FacetFiltersForm.upgradeAboveFoldImages());
      } else {
        FacetFiltersForm.upgradeAboveFoldImages();
      }
      // Fired AFTER the new grid is really in the DOM (facets:filters-updated
      // fires at fetch time, up to preloadAboveFold's cap earlier) — the sticky
      // header re-syncs its pinned state against the settled layout on this.
      document.dispatchEvent(new CustomEvent('facets:grid-swapped'));
    };

    // Cached render: the section HTML + image BYTES are warm, but the freshly-created
    // decoding="async" <img>s still decode off the main thread, so a plain innerHTML
    // swap paints them blank-then-image (popcorn). Pre-decode the above-the-fold
    // images first (cheap — bytes are HTTP-cached) so the cached grid reveals
    // already-painted. No dim: the outgoing grid stays fully visible for the brief,
    // capped decode, so there's no loading flash.
    if (instant) {
      FacetFiltersForm.preloadAboveFold(parsedContainer, 300).then(() => {
        if (generation !== FacetFiltersForm.swapGeneration) return;
        apply();
      });
      return;
    }

    container.classList.add('is-swapping'); // dim the outgoing grid (stays on screen — no blank)
    // Preload + decode the incoming above-the-fold images, then reveal the whole grid
    // at once. Generous cap: the old grid stays visible (dimmed) the whole time, so
    // this only bounds a pathologically slow/broken image.
    FacetFiltersForm.preloadAboveFold(parsedContainer, 1500).then(() => {
      // A newer swap superseded this one (user clicked again during the load) — drop
      // this stale result instead of overwriting the newer grid.
      if (generation !== FacetFiltersForm.swapGeneration) return;
      apply();
    });
  }

  // Eager-load the first two rows up front instead of lazily, so the collection's
  // initial above-the-fold grid doesn't pop in image-by-image. First two rows =
  // 8 cards on desktop (4-up), 4 on mobile (2-up). card-product renders only the
  // first 2 eager, so this upgrades the rest of the first two rows. Setting eager
  // on the real card <img> is what forces the load (a preload wouldn't paint it).
  static upgradeAboveFoldImages() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const eagerCount = FacetFiltersForm.isDesktopMQL.matches ? 8 : 4;
    const items = Array.from(container.querySelectorAll('#product-grid > li')).slice(0, eagerCount);
    items.forEach((li) => {
      const img =
        li.querySelector('.card-image-slider__list .slider__slide:first-child img') || li.querySelector('img');
      if (img && img.getAttribute('loading') !== 'eager') {
        img.setAttribute('loading', 'eager');
        img.loading = 'eager';
      }
    });
  }

  static renderProductCount(parsed) {
    const parsedCount = parsed.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container && parsedCount) {
      container.innerHTML = parsedCount.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop && parsedCount) {
      containerDesktop.innerHTML = parsedCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsed, event) {
    const facetDetailsElementsFromFetch = parsed.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsed);
    FacetFiltersForm.renderAdditionalElements(parsed);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    // Carry Shopify's alternate-template param (?view=) through the rewrite.
    // It isn't a facet param, so rebuilding the query string from filter state
    // alone strips it — and a reload/back-nav of the stripped URL would serve
    // the default template mid-A/B-test (Intelligems v2/v3 template test).
    const params = new URLSearchParams(searchParams);
    if (!params.has('view')) {
      const view = new URLSearchParams(window.location.search).get('view');
      if (view) params.set('view', view);
    }
    const queryString = params.toString();
    history.pushState(
      { searchParams: queryString },
      '',
      `${window.location.pathname}${queryString && '?'.concat(queryString)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const isMobileCheckbox = event.srcElement.className == 'mobile-facets__checkbox';
    const isMobile = isMobileCheckbox || event.target.closest('form').id === 'FacetFiltersFormMobile';

    const relevantForms = [];
    sortFilterForms.forEach((form) => {
      if (isMobile) {
        if (form.id === 'FacetFiltersFormMobile') relevantForms.push(form);
      } else if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
        relevantForms.push(form);
      }
    });

    // Preserve URL params not owned by any of the active facet forms (e.g. quick-filter pill params).
    const merged = new URLSearchParams(window.location.search);
    merged.delete('page');
    const ownedKeys = new Set();
    relevantForms.forEach((form) => {
      form.querySelectorAll('[name]').forEach((input) => ownedKeys.add(input.name));
    });
    ownedKeys.forEach((key) => merged.delete(key));
    relevantForms.forEach((form) => {
      new FormData(form).forEach((value, key) => merged.append(key, value));
    });

    this.onSubmitForm(merged.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.FILTER_CACHE_MAX = 8;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
// Eager-load the first two rows of the initial (server-rendered) collection grid so
// it doesn't pop in image-by-image above the fold. Guarded to the collection page.
if (document.querySelector('facet-filters-form')) FacetFiltersForm.upgradeAboveFoldImages();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);